import sanitizeHtml from 'sanitize-html';
import linkifyHtml from 'linkify-html';

const removeEmptyExclusionFilter = frame => ['div', 'p', 'span'].indexOf(frame.tag) > -1
  && !frame.text.trim()
  && (!frame.mediaChildren || frame.mediaChildren.length === 0); // Doesn't have any pictures included

// Disallow everything special in excerpts
export const guidancePostExcerptConfig = { allowedTags: [] };
export const sanitizeGuidancePostExcerpt = text => (text
  ? sanitizeHtml(text, guidancePostExcerptConfig)
  : '');

export const guidancePostConfig = {
  allowedTags: sanitizeHtml.defaults.allowedTags.concat(['span', 'sup', 'img']),
  allowedAttributes: {
    a: ['href', 'name', 'target', 'ref'],
    img: ['src', 'alt', 'srcset'],
    '*': ['style'],
  },
  selfClosing: ['img'],
  allowedStyles: {
    '*': {
      color: [/^(?!#000000).*$/], // Ignore black. Use default
      'font-size': [/^[0-9](\.[0-9]*)?[a-zA-Z%]*$/], // Allow only numbers smaller than 10, whatever unit...
      'text-align': [/^justify$/, /^center$/, /^right$/, /^left$/],
      'text-decoration': [/^underline$/],
      'font-weight': [/^.*$/],
      'font-style': [/^italic$/],
      'vertical-align': [/^super$/],
    },
  },
  exclusiveFilter: removeEmptyExclusionFilter,
};
export const sanitizeGuidancePostBody = text => sanitizeHtml(text, guidancePostConfig);

export const genericConfigValidElements = [
  'h2',
  'h3',
  'p',
  'blockquote',
  'a[href|target=_blank|rel<noopener?nofollow?nofollow]',
  'ul',
  'li',
  'ol',
  'b',
  'i',
  'strong',
  'em',
  'u',
  'strike',
  'br',
].join(',');

const generic = {
  allowedTags: 'h2, h3, p, blockquote, a, ul, li, ol, b, i, strong, em, u, strike, br',
  allowedAttributes: {
    a: ['href', 'name', 'target', 'rel', 'data-user-content'],
  },
  allowedSchemes: ['http', 'https', 'mailto', 'tel', 'sms', 'geo'],
  exclusiveFilter: removeEmptyExclusionFilter,
  transformTags: {
    a: (tagName, attribs) => ({
      tagName,
      attribs: {
        ...attribs,
        'data-user-content': true,
      },
    }),
  },
};

const lessonBodyAllowedStyles = {
  div: ['id', 'top', 'left', 'position', 'padding-bottom', 'max-width'],
  iframe: ['width', 'height', 'position'],
  ul: ['list-style-type'],
  li: ['color'],
  img: ['vertical-align', 'max-height', 'max-width'],
  td: ['background-color', 'background'],
  a: ['color'],
  p: ['color'],
  span: ['color', 'background-color'],
};

const getLessonAllowedRegex = (allowedStyles) => !allowedStyles || allowedStyles?.length === 0
  ? new RegExp('^$')
  : new RegExp(`(?:${(allowedStyles).join('|')}):[\\s\\w\\d.,()%]+`);

const parseLessonInlineStyles = (style, allowedStyles) => style.split(';')
  .map(e => e.trim())
  .filter(e => getLessonAllowedRegex(allowedStyles).test(e)).join('; ');

const parseStyles = (attribs, allowedStyles) => {

  const result = attribs;

  if (attribs.style) {
    const parsedStyles = parseLessonInlineStyles(attribs.style, allowedStyles);

    if (!parsedStyles) {
      delete result.style;
    } else {
      result.style = parsedStyles;
    }
  }

  return result;
};

// Links in user content open outside the app, and the app's own click handler finds them by their mark
const userLink = (tagName, attribs) => ({
  tagName,
  attribs: {
    ...attribs,
    ...(attribs.href?.[0] !== '#' && { target: '_blank' }),
    'data-user-content': true,
    rel: 'noopener noreferrer nofollow',
  },
});

const lessonBodySanitizationRules = {
  allowedTags: 'h1, h2, h3, h4, p, blockquote, a, ul, li, ol, b, i, strong, em, u, strike, br, img, iframe, table, thead, th, tbody, tr, td, hr, audio, figure, div, span, video, source',
  allowedAttributes: {
    '*': ['data-ephox-embed-iri', 'data-guava-attachment-id'],

    a: ['href', 'name', 'target', 'rel', 'data-user-content'],
    ol: ['type', 'start'],
    ul: ['style'],
    img: ['src', 'alt', 'srcset', 'width', 'style'],
    table: ['style'],
    td: ['width', 'colspan', 'rowspan', 'align', 'style'],
    th: ['align', 'colspan'],
    iframe: ['width', 'height', 'src', 'frameborder', 'allow', 'allowfullscreen', 'style'],
    audio: ['src', 'controls'],
    figure: ['class', 'style'],
    div: ['id', 'class', 'style', 'data-oembed-url'],
    p: ['style'],
    span: ['id', 'class', 'style'],

    video: ['width', 'height', 'controls', 'poster'],
    source: ['src', 'type'],
  },
  // allowedSchemes: ['http', 'https', 'mailto', 'tel', 'sms', 'geo'],
  parseStyleAttributes: false, // Style parsing doesn't work in browsers!
  allowedClasses: {
    figure: ['media', 'image', 'image_resized'],
    div: ['mce-non-editable', 'mceNonEditable'],
    span: ['mce-non-editable', 'mceNonEditable'],
  },
  transformTags: {
    // TODO: Iframe styles, resolve for all and disallow styles
    div: (tagName, attribs) => ({ tagName, attribs: parseStyles(attribs, ['top', 'left', 'position', 'padding-bottom', 'max-width']) }),
    ul: (tagName, attribs) => ({ tagName, attribs: parseStyles(attribs, ['list-style-type']) }),
    li: (tagName, attribs) => ({ tagName, attribs: parseStyles(attribs, ['color']) }),
    img: (tagName, attribs) => ({ tagName, attribs: parseStyles(attribs, ['vertical-align', 'max-height', 'max-width']) }),
    table: (tagName, attribs) => ({ tagName, attribs: parseStyles(attribs, []) }),
    td: (tagName, attribs) => ({ tagName, attribs: parseStyles(attribs, ['background-color', 'background']) }),
    iframe: (tagName, attribs) => ({ tagName, attribs: parseStyles(attribs, ['width', 'height', 'position']) }),
    figure: (tagName, attribs) => ({ tagName, attribs: parseStyles(attribs, []) }),
    p: (tagName, attribs) => ({ tagName, attribs: parseStyles(attribs, ['color']) }),
    span: (tagName, attribs) => ({ tagName, attribs: parseStyles(attribs, ['color', 'background-color']) }),
    a: (tagName, attribs) => userLink(tagName, parseStyles(attribs, ['color'])),
  },
};

export const lessonBodyTinyMceValidElements = lessonBodySanitizationRules.allowedTags.split(',').map(tag => {
  const allowedTag = tag.trim();
  const allowedAttributes = lessonBodySanitizationRules.allowedAttributes[allowedTag] || [];

  return allowedAttributes.length > 0 ? `${allowedTag}[${allowedAttributes.join('|')}]` : allowedTag;
}).join(',');

export const lessonBodyTinyMceValidStyles = Object.entries(lessonBodyAllowedStyles)
  .reduce((acc, [key, value]) => ({
    ...acc,
    [key]: value.join(','),
  }), {});

export const pageContentSanitizationRules = lessonBodySanitizationRules;
export const pageContentTinyMceValidElements = lessonBodyTinyMceValidElements;
export const pageContentTinyMceValidStyles = lessonBodyTinyMceValidStyles;

export const sanitizePostBody = (text, config) => text ? sanitizeHtml(text, { ...generic, ...config }) : text;

// A post body renders with css pre-wrap, so the newlines an editor leaves between block
// tags would come out as blank lines.
export const sanitizePostHtml = (html) => sanitizePostBody(html, {
  textFilter: (text) => text.replace(/(?:\r\n|\r|\n)/g, ''),
});

const linkifyOptions = {
  target: {
    url: '_blank',
  },
  events: {
    onClick: e => e.stopPropagation(),
  },
  attributes: {
    rel: 'noopener noreferrer nofollow',
  },
};

const encodeHtml = (str) => (str || '').replace(/[\u00A0-\u9999<>&]/g, (i) => `&#${i.charCodeAt(0)};`);

// A body with no html of its own: the characters are encoded before any url in it
// becomes a link.
export const sanitizePostText = (text) => sanitizePostBody(linkifyHtml(encodeHtml(text), linkifyOptions));

export const sanitizeWorkspaceProfile = (text, config) => text ? sanitizeHtml(text, { ...generic, ...config }) : text;
export const sanitizeEventDescription = (text, config) => text ? sanitizeHtml(text, { ...generic, ...config }) : text;
export const sanitizeCourseDescription = (text, config) => text ? sanitizeHtml(text, { ...generic, ...config }) : text;
export const sanitizeQuizExplanation = (text, config) => text ? sanitizeHtml(text, { ...generic, ...config }) : text;
export const sanitizeFormDescription = (text, config) => text ? sanitizeHtml(text, { ...generic, ...config }) : text;

// Only rendering lets the backend's heading marks through: the editor's valid elements
// come from the shared rules, so it still drops them and never saves them
const headingMarks = Object.fromEntries(['h1', 'h2', 'h3', 'h4'].map(tag => [tag, ['data-heading']]));

export const sanitizePageContent = (text, config) => text ? sanitizeHtml(text, {
  ...pageContentSanitizationRules,
  allowedAttributes: { ...pageContentSanitizationRules.allowedAttributes, ...headingMarks },
  ...config,
}) : text;
const noticeSanitizationRules = {
  allowedTags: ['h2', 'h3', 'h4', 'p', 'blockquote', 'a', 'ul', 'ol', 'li', 'b', 'i', 'strong', 'em', 'u', 'br', 'hr', 'img', 'iframe'],
  allowedAttributes: {
    a: ['href', 'target', 'rel', 'data-user-content'],
    img: ['src', 'alt'],
    iframe: ['src', 'title', 'allow', 'allowfullscreen', 'referrerpolicy', 'loading'],
  },
  allowedSchemes: ['http', 'https', 'mailto', 'tel'],
  allowedSchemesByTag: { img: ['https'], iframe: ['https'] },
  allowProtocolRelative: false,
  allowedIframeHostnames: ['www.youtube.com', 'www.youtube-nocookie.com', 'player.vimeo.com'],
  allowIframeRelativeUrls: false,
  transformTags: {
    a: userLink,
  },
  // A frame from a host that is not allowed loses its src, which would leave an empty box
  exclusiveFilter: frame => frame.tag === 'iframe' && !frame.attribs.src,
};

export const sanitizeNoticeHtml = text => (text ? sanitizeHtml(text, noticeSanitizationRules) : '');

export const sanitizeLessonBody = (text, config) => text ? sanitizeHtml(text, { ...lessonBodySanitizationRules, ...config }) : text;
export const sanitizeSectionDescription = (text, config) => text ? sanitizeHtml(text, { ...generic, ...config }) : text;

export const sanitizeLinkify = (text, config) => sanitizeHtml(text, {
  allowedTags: 'a',
  allowedAttributes: {
    a: ['href', 'name', 'target', 'rel'],
  },
  allowedSchemes: ['http', 'https', 'mailto', 'tel', 'sms', 'geo'],
  disallowedTagsMode: 'escape',
  ...config,
});
